import type { TrainerInfo, TrainerTier } from "./types";

/**
 * Decoded FTMS Indoor Bike Data (0x2AD2).
 *
 * All fields are optional because the characteristic's flags field decides
 * which values are present in a given notification.
 */
export interface IndoorBikeData {
  speedKmh?: number;
  averageSpeedKmh?: number;
  cadenceRpm?: number;
  averageCadenceRpm?: number;
  totalDistanceM?: number;
  resistanceLevel?: number;
  powerWatts?: number;
  averagePowerWatts?: number;
  totalEnergyKcal?: number;
  heartRateBpm?: number;
  metabolicEquivalent?: number;
  elapsedTimeS?: number;
  remainingTimeS?: number;
}

/** A six-byte FTMS supported range: minimum, maximum, and increment. */
export interface SupportedRange {
  min: number;
  max: number;
  increment: number;
}

/**
 * The result code in a Fitness Machine Control Point response (0x80).
 * See FTMS 1.0, Table 4.22.
 */
export type ControlPointResult =
  | "success"
  | "not-supported"
  | "invalid-parameter"
  | "operation-failed"
  | "control-not-permitted"
  | "unknown";

export interface ControlPointResponse {
  requestOpcode: number;
  resultCode: number;
  success: boolean;
  result: ControlPointResult;
  /** Any response parameters after the result code (usually empty). */
  parameter: Uint8Array;
}

/**
 * Fitness Machine Control Point opcodes differ by procedure, not by trainer.
 *
 * The standard FTMS 1.0 map is the one the Victory uses. The "brief" map is
 * kept because the Phase 0 brief and the original encoder used those numbers;
 * it is only selected when a trainer rejects the standard Start opcode.
 */
export interface FtmsOpcodeProfile {
  targetResistance: number;
  targetPower: number;
  start: number;
  stop: number;
  simulationParameters: number;
  spinDown: number;
}

export const FTMS_STANDARD_OPCODES: FtmsOpcodeProfile = {
  targetResistance: 0x04,
  targetPower: 0x05,
  start: 0x07,
  stop: 0x08,
  simulationParameters: 0x11,
  spinDown: 0x13,
};

export const FTMS_BRIEF_OPCODES: FtmsOpcodeProfile = {
  targetResistance: 0x02,
  targetPower: 0x03,
  start: 0x05,
  stop: 0x06,
  simulationParameters: 0x07,
  spinDown: 0x09,
};

export type SpinDownState = "requested" | "success" | "error" | "stopped" | "unknown";

/** A decoded Fitness Machine Status notification (0x2ADA). */
export type FitnessMachineStatus =
  | { opcode: 0x01; type: "reset" }
  | { opcode: 0x02; type: "stopped" }
  | { opcode: 0x02; type: "paused" }
  | { opcode: 0x03; type: "safety-stop" }
  | { opcode: 0x04; type: "started" }
  | { opcode: 0x05; type: "target-speed"; speedKmh: number }
  | { opcode: 0x06; type: "target-inclination"; gradePct: number }
  | { opcode: 0x07; type: "target-resistance"; resistanceLevel: number }
  | { opcode: 0x08; type: "target-power"; watts: number }
  | { opcode: 0x09; type: "target-heart-rate"; bpm: number }
  | { opcode: 0x0a; type: "target-expended-energy"; kcal: number }
  | { opcode: 0x0b; type: "target-step-rate"; stepsPerMinute: number }
  | { opcode: 0x0c; type: "target-stride-count"; strides: number }
  | { opcode: 0x0d; type: "target-distance"; distanceM: number }
  | { opcode: 0x0e; type: "target-training-time"; seconds: number }
  | {
      opcode: 0x12;
      type: "simulation-parameters";
      gradePct: number;
      windMps: number;
      crr: number;
      cda: number;
    }
  | { opcode: 0x13; type: "wheel-circumference"; circumferenceMm: number }
  | { opcode: 0x14; type: "spin-down"; status: SpinDownState }
  | { opcode: 0x15; type: "cadence"; cadenceRpm: number }
  | { opcode: number; type: "unknown"; data: Uint8Array };

/**
 * The Fitness Machine Feature characteristic (0x2ACC).
 *
 * The raw 32-bit masks are kept for forward compatibility; the named booleans
 * are the bits this package uses.
 */
export interface FitnessMachineFeature {
  machineFeatures: number;
  targetFeatures: number;
  averageSpeed: boolean;
  cadence: boolean;
  totalDistance: boolean;
  resistanceLevel: boolean;
  heartRate: boolean;
  elapsedTime: boolean;
  powerMeasurement: boolean;
  targetSpeed: boolean;
  targetInclination: boolean;
  targetResistance: boolean;
  targetPower: boolean;
  targetHeartRate: boolean;
  targetExpendedEnergy: boolean;
  targetSimulation: boolean;
  targetWheelCircumference: boolean;
  targetSpinDown: boolean;
  targetCadence: boolean;
}

function requireBytes(view: DataView, offset: number, length: number, label: string): void {
  if (offset + length > view.byteLength) {
    throw new Error(`${label} truncated`);
  }
}

/**
 * Parse the FTMS Indoor Bike Data characteristic (0x2AD2).
 *
 * Flags (FTMS 1.0, Table 4.8):
 *   0 more data (0 = instantaneous speed present), 1 average speed,
 *   2 instantaneous cadence, 3 average cadence, 4 total distance (u24),
 *   5 resistance level (s16, unitless), 6 instantaneous power (s16),
 *   7 average power (s16), 8 expended energy (u16/u16/u8),
 *   9 heart rate (u8), 10 metabolic equivalent (u8, 0.1),
 *   11 elapsed time (u16 s), 12 remaining time (u16 s).
 */
export function parseIndoorBikeData(view: DataView): IndoorBikeData {
  if (view.byteLength < 2) throw new Error("indoor bike data too short");
  const flags = view.getUint16(0, true);
  let offset = 2;
  const data: IndoorBikeData = {};

  const readUint16 = (): number => {
    requireBytes(view, offset, 2, "indoor bike data");
    const value = view.getUint16(offset, true);
    offset += 2;
    return value;
  };
  const readInt16 = (): number => {
    requireBytes(view, offset, 2, "indoor bike data");
    const value = view.getInt16(offset, true);
    offset += 2;
    return value;
  };
  const skip = (bytes: number): void => {
    requireBytes(view, offset, bytes, "indoor bike data");
    offset += bytes;
  };

  // Bit 0 = more data; zero means instantaneous speed is present.
  if ((flags & 0x0001) === 0) data.speedKmh = readUint16() / 100;
  if (flags & 0x0002) data.averageSpeedKmh = readUint16() / 100;
  if (flags & 0x0004) data.cadenceRpm = readUint16() / 2;
  if (flags & 0x0008) data.averageCadenceRpm = readUint16() / 2;
  if (flags & 0x0010) {
    requireBytes(view, offset, 3, "indoor bike data");
    data.totalDistanceM =
      view.getUint8(offset) | (view.getUint8(offset + 1) << 8) | (view.getUint8(offset + 2) << 16);
    offset += 3;
  }
  if (flags & 0x0020) data.resistanceLevel = readInt16();
  if (flags & 0x0040) data.powerWatts = readInt16();
  if (flags & 0x0080) data.averagePowerWatts = readInt16();
  if (flags & 0x0100) {
    data.totalEnergyKcal = readUint16();
    skip(3); // energy per hour (u16) and energy per minute (u8)
  }
  if (flags & 0x0200) {
    requireBytes(view, offset, 1, "indoor bike data");
    data.heartRateBpm = view.getUint8(offset);
    offset += 1;
  }
  if (flags & 0x0400) {
    requireBytes(view, offset, 1, "indoor bike data");
    data.metabolicEquivalent = view.getUint8(offset) / 10;
    offset += 1;
  }
  if (flags & 0x0800) data.elapsedTimeS = readUint16();
  if (flags & 0x1000) data.remainingTimeS = readUint16();

  return data;
}

export function encodeRequestControl(): Uint8Array {
  return Uint8Array.of(0x00);
}

export function encodeStart(opcode = FTMS_STANDARD_OPCODES.start): Uint8Array {
  return Uint8Array.of(opcode);
}

/**
 * Stop or pause. The parameter is mandatory:
 * 0x01 = stop, 0x02 = pause.
 */
export function encodeStop(
  pause = false,
  opcode = FTMS_STANDARD_OPCODES.stop,
): Uint8Array {
  return Uint8Array.of(opcode, pause ? 0x02 : 0x01);
}

/** Target resistance level in percent (0.1 resolution on the wire). */
export function encodeTargetResistance(
  levelPercent: number,
  opcode = FTMS_STANDARD_OPCODES.targetResistance,
): Uint8Array {
  const value = Math.max(0, Math.min(1000, Math.round(levelPercent * 10)));
  return Uint8Array.of(opcode, value & 0xff, (value >> 8) & 0xff);
}

/** Target power in watts. ERG fallback when simulation parameters fail. */
export function encodeTargetPower(
  watts: number,
  opcode = FTMS_STANDARD_OPCODES.targetPower,
): Uint8Array {
  const value = Math.max(-32768, Math.min(32767, Math.round(watts)));
  return Uint8Array.of(opcode, value & 0xff, (value >> 8) & 0xff);
}

/**
 * Set Indoor Bike Simulation Parameters (0x11 in standard FTMS).
 * wind 0.001 m/s, grade 0.01 %, Crr 0.0001, Cw 0.01 kg/m.
 * The `cda` argument name is kept for compatibility with the sim's CdA value.
 */
export function encodeSimParameters(
  gradePct: number,
  crr = 0.004,
  cda = 0.36,
  windMps = 0,
  opcode = FTMS_STANDARD_OPCODES.simulationParameters,
): Uint8Array {
  const wind = Math.max(-32768, Math.min(32767, Math.round(windMps * 1000)));
  const grade = Math.max(-32768, Math.min(32767, Math.round(gradePct * 100)));
  const rolling = Math.max(0, Math.min(255, Math.round(crr * 10000)));
  const drag = Math.max(0, Math.min(255, Math.round(cda * 100)));
  return Uint8Array.of(
    opcode,
    wind & 0xff,
    (wind >> 8) & 0xff,
    grade & 0xff,
    (grade >> 8) & 0xff,
    rolling,
    drag,
  );
}

/** Start spin-down; 0x01 means start, 0x02 means ignore. */
export function encodeSpinDownStart(
  opcode = FTMS_STANDARD_OPCODES.spinDown,
): Uint8Array {
  return Uint8Array.of(opcode, 0x01);
}

export function encodeSpinDownIgnore(
  opcode = FTMS_STANDARD_OPCODES.spinDown,
): Uint8Array {
  return Uint8Array.of(opcode, 0x02);
}

export function controlPointResult(resultCode: number): ControlPointResult {
  switch (resultCode) {
    case 0x01:
      return "success";
    case 0x02:
      return "not-supported";
    case 0x03:
      return "invalid-parameter";
    case 0x04:
      return "operation-failed";
    case 0x05:
      return "control-not-permitted";
    default:
      return "unknown";
  }
}

export function controlPointResultMessage(resultCode: number): string {
  switch (resultCode) {
    case 0x01:
      return "success";
    case 0x02:
      return "operation not supported";
    case 0x03:
      return "invalid parameter";
    case 0x04:
      return "operation failed";
    case 0x05:
      return "control not permitted";
    default:
      return `unknown result code 0x${resultCode.toString(16).padStart(2, "0")}`;
  }
}

/** Parse a Fitness Machine Control Point indication (0x80 request opcode). */
export function parseControlPointResponse(view: DataView): ControlPointResponse {
  if (view.byteLength < 3) throw new Error("control point response too short");
  const responseOpcode = view.getUint8(0);
  if (responseOpcode !== 0x80) throw new Error("not a control point response");
  const requestOpcode = view.getUint8(1);
  const resultCode = view.getUint8(2);
  return {
    requestOpcode,
    resultCode,
    success: resultCode === 0x01,
    result: controlPointResult(resultCode),
    parameter: new Uint8Array(view.buffer, view.byteOffset + 3, view.byteLength - 3).slice(),
  };
}

/** Parse a Fitness Machine Status notification (0x2ADA). */
export function parseFitnessMachineStatus(view: DataView): FitnessMachineStatus {
  if (view.byteLength < 1) throw new Error("fitness machine status too short");
  const opcode = view.getUint8(0);
  const require = (bytes: number): void => requireBytes(view, 1, bytes, "fitness machine status");
  const uint8 = (): number => {
    require(1);
    return view.getUint8(1);
  };
  const uint16 = (): number => {
    require(2);
    return view.getUint16(1, true);
  };
  const int16 = (): number => {
    require(2);
    return view.getInt16(1, true);
  };

  switch (opcode) {
    case 0x01:
      return { opcode: 0x01, type: "reset" };
    case 0x02: {
      const state = uint8();
      if (state === 0x01) return { opcode: 0x02, type: "stopped" };
      if (state === 0x02) return { opcode: 0x02, type: "paused" };
      break;
    }
    case 0x03:
      return { opcode: 0x03, type: "safety-stop" };
    case 0x04:
      return { opcode: 0x04, type: "started" };
    case 0x05:
      return { opcode: 0x05, type: "target-speed", speedKmh: uint16() / 100 };
    case 0x06:
      return { opcode: 0x06, type: "target-inclination", gradePct: int16() / 10 };
    case 0x07:
      return { opcode: 0x07, type: "target-resistance", resistanceLevel: int16() / 10 };
    case 0x08:
      return { opcode: 0x08, type: "target-power", watts: int16() };
    case 0x09:
      return { opcode: 0x09, type: "target-heart-rate", bpm: uint8() };
    case 0x0a:
      return { opcode: 0x0a, type: "target-expended-energy", kcal: uint16() };
    case 0x0b:
      return { opcode: 0x0b, type: "target-step-rate", stepsPerMinute: uint16() / 2 };
    case 0x0c:
      return { opcode: 0x0c, type: "target-stride-count", strides: uint16() };
    case 0x0d:
      require(3);
      return {
        opcode: 0x0d,
        type: "target-distance",
        distanceM:
          view.getUint8(1) | (view.getUint8(2) << 8) | (view.getUint8(3) << 16),
      };
    case 0x0e:
      return { opcode: 0x0e, type: "target-training-time", seconds: uint16() };
    case 0x12:
      require(6);
      return {
        opcode: 0x12,
        type: "simulation-parameters",
        windMps: view.getInt16(1, true) / 1000,
        gradePct: view.getInt16(3, true) / 100,
        crr: view.getUint8(5) / 10000,
        cda: view.getUint8(6) / 100,
      };
    case 0x13:
      return { opcode: 0x13, type: "wheel-circumference", circumferenceMm: uint16() / 10 };
    case 0x14: {
      const state = uint8();
      const status: SpinDownState =
        state === 0x01
          ? "requested"
          : state === 0x02
            ? "success"
            : state === 0x03
              ? "error"
              : state === 0x04
                ? "stopped"
                : "unknown";
      return { opcode: 0x14, type: "spin-down", status };
    }
    case 0x15:
      return { opcode: 0x15, type: "cadence", cadenceRpm: uint16() / 2 };
    default:
      break;
  }

  return {
    opcode,
    type: "unknown",
    data: new Uint8Array(view.buffer, view.byteOffset, view.byteLength).slice(),
  };
}

/** Parse the Fitness Machine Feature characteristic (0x2ACC). */
export function parseFitnessMachineFeature(view: DataView): FitnessMachineFeature {
  if (view.byteLength < 4) throw new Error("fitness machine feature too short");
  const machineFeatures = view.getUint32(0, true);
  const targetFeatures = view.byteLength >= 8 ? view.getUint32(4, true) : 0;
  const machine = (bit: number): boolean => ((machineFeatures >>> bit) & 1) === 1;
  const target = (bit: number): boolean => ((targetFeatures >>> bit) & 1) === 1;

  return {
    machineFeatures,
    targetFeatures,
    averageSpeed: machine(0),
    cadence: machine(1),
    totalDistance: machine(2),
    resistanceLevel: machine(7),
    heartRate: machine(10),
    elapsedTime: machine(12),
    powerMeasurement: machine(14),
    targetSpeed: target(0),
    targetInclination: target(1),
    targetResistance: target(2),
    targetPower: target(3),
    targetHeartRate: target(4),
    targetExpendedEnergy: target(5),
    targetSimulation: target(13),
    targetWheelCircumference: target(14),
    targetSpinDown: target(15),
    targetCadence: target(16),
  };
}

/**
 * Parse Supported Inclination Range (0x2AD5): s16 min/max and u16 increment,
 * all in 0.1 %.
 */
export function parseSupportedInclinationRange(view: DataView): SupportedRange {
  if (view.byteLength < 6) throw new Error("supported inclination range too short");
  return {
    min: view.getInt16(0, true) / 10,
    max: view.getInt16(2, true) / 10,
    increment: view.getUint16(4, true) / 10,
  };
}

/** Parse Supported Resistance Level Range (0x2AD6), in 0.1 units. */
export function parseSupportedResistanceRange(view: DataView): SupportedRange {
  if (view.byteLength < 6) throw new Error("supported resistance range too short");
  return {
    min: view.getInt16(0, true) / 10,
    max: view.getInt16(2, true) / 10,
    increment: view.getUint16(4, true) / 10,
  };
}

/** Parse Supported Power Range (0x2AD8), in whole watts. */
export function parseSupportedPowerRange(view: DataView): SupportedRange {
  if (view.byteLength < 6) throw new Error("supported power range too short");
  return {
    min: view.getInt16(0, true),
    max: view.getInt16(2, true),
    increment: view.getUint16(4, true),
  };
}

export function trainerInfoFromDevice(
  id: string,
  name: string,
  firmware?: string,
  tier: TrainerTier = "A",
): TrainerInfo {
  return { id, name, firmware, tier };
}
